/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import {Row, Col, OverlayTrigger, Tooltip} from "react-bootstrap";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../resources/validations/validations";
import { usePeopleService } from "../../../services/peopleService";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from "../../../contexts/userContext";
import { LEAVE_COMPANY } from "../../../constants/routes";
import { getTripitLink } from "../../../helpers/general";

/* Components */
import UCButton from "../../buttons";
import UCSelect from "../../ucselect";
import UCCheckbox from "../../uccheckbox";
import LeaveCompanyModal from "../../modals/leaveCompanyModal";
import WarningModal from "../../modals/warningModal";
import NotificationPreference from "../../notificationPreference";
import { ReactComponent as UserDeleteIcon } from "../../../resources/images/user-x.svg";
import { ReactComponent as DeleteUserIconRed } from "../../../resources/images/user-icon-red.svg";
import { ReactComponent as HelpIcon } from "../../../resources/images/help.svg";
import { ReactComponent as AALogoSmall } from "../../../resources/images/aa-logo-small-no-text.svg";
import UpdateEmailModal from "../../modals/updateEmailModal";
import { ReactComponent as EditEmailSvg } from '../../../resources/images/edit.svg';
import EditCitiPCANsModal from "../../modals/editCitiPCANsModal";

/* Styles */
import styles from "./styles.js"
import {colors} from "../../../constants/colors";
import generalStyles from "../../../resources/styles/general";


const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'traveler.details.firstNamePlaceholder'
  },
  lastNamePlaceholder: {
    id: 'traveler.details.lastNamePlaceholder'
  },
  emailPlaceholder: {
    id: 'traveler.details.emailPlaceholder'
  },
  aadvantagePlaceholder: {
    id: 'traveler.details.aadvantagePlaceholder'
  },
  aupcanPlaceholderMessage: {
    id: 'traveler.details.aupcan'
  },
  entityPCANPlaceholderMessage: {
    id: 'traveler.details.entityPCAN'
  },
})

const PersonalProfileAA = ({travelerDetails, teams, roles}) => {
  const theme = useTheme();
  const classes = styles({theme});
  const general = generalStyles({ theme });
  const intl = useIntl();
  const navigate = useNavigate();

  const formRef = useRef(null);

  const { travelerDetailsMockedApiValidationObj } = useValidationService()
  const [travelerSchema, setTravelerSchema] = useState({});

  const [showLeaveCompanyModal, setShowLeaveCompanyModal] = useState(false);
  const [showRemoveAdminOnlyModal, setShowRemoveAdminOnlyModal] = useState(false);
  const [showRemoveOwnerOnlyModal, setShowRemoveOwnerOnlyModal] = useState(false);
  const [showRemoveCitiOnlyModal, setShowRemoveCitiOnlyModal] = useState(false);
  const [tripLinkCheckbox, setTripLinkCheckbox] = useState(travelerDetails?.triplink || false);
  const [businessSummaryCheckbox, setBusinessSummaryCheckbox] = useState(travelerDetails?.marketingPreferences?.marketingBusinessSummary || false);
  const [promotionCheckbox, setPromotionCheckbox] = useState(travelerDetails?.marketingPreferences?.marketingPromotions || false);
  const [newsAndOffersCheckbox, setNewsAndOffersCheckbox] = useState(travelerDetails?.marketingPreferences?.marketingNewsAndOffers || false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);
  const [showEditPcanModal, setShowEditPcaModal] = useState(false);
  const [primaryPCANField, setPrimaryField ] = useState(travelerDetails?.primaryPCAN)
  const [auPCANField, setAuPCANField ] = useState(travelerDetails?.auPCAN)

  const { updatePeople, leaveCompany } = usePeopleService();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode
  
  useEffect(() => {
    (async () => {
      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(travelerDetailsMockedApiValidationObj);
      setTravelerSchema(result)
    })();
  }, []);

  const goBack = () => {
    setTripLinkCheckbox(travelerDetails?.triplink || false);
    setBusinessSummaryCheckbox(travelerDetails?.marketingPreferences?.marketingBusinessSummary || false);
    setPromotionCheckbox(travelerDetails?.marketingPreferences?.marketingPromotions || false);
    setNewsAndOffersCheckbox(travelerDetails?.marketingPreferences?.marketingNewsAndOffers || false);
    navigate(-1);
  }

  const leaveAction = async () => {
    const response = await leaveCompany({
      entityID: travelerDetails?.userEntityId,
    })
    if (response) {
      setShowLeaveCompanyModal(false)
      navigate('../' + LEAVE_COMPANY);
    }
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values) => {
    await updatePeople({
      userId: travelerDetails?.userEntityId,
      roleId: values?.roleId,
      groupEntityId: values?.groupId,
      isTripLinkAccount: tripLinkCheckbox,
      marketingBusinessSummary: businessSummaryCheckbox,
      marketingPromotions: promotionCheckbox,
      marketingNewsAndOffers: newsAndOffersCheckbox
    })
  }

  const checkCanChangeRole = () => {
    if(!travelerDetails?.canChangeRole) {    
      setShowRemoveOwnerOnlyModal(true)
    }
  }

  const checkIfCanRemoveUser = () => {
    if(travelerDetails?.canRemoveUser) {
      setShowLeaveCompanyModal(true);
    } else if(travelerDetails?.companyInfo?.companyType ==="Citi"){
      setShowRemoveCitiOnlyModal(true);
    } else {
      setShowRemoveAdminOnlyModal(true);
    }
  }

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    travelerDetails &&
    <div className={classes.mainDiv}>
      {
        travelerDetails?.membershipNumber && 
        <Formik
          innerRef={formRef}
          enableReinitialize={true}
          initialValues={{
            aadvantage: `#${travelerDetails?.membershipNumber}`,
            firstName: travelerDetails?.firstName,
            lastName: travelerDetails?.lastName,            
            roleId: travelerDetails?.companyInfo?.roleId,
            groupId: travelerDetails?.companyInfo?.groupEntityId,
            emailAddress: travelerDetails?.emailAddress,
            auPCAN: auPCANField ? auPCANField  : "",
            primaryPCAN: primaryPCANField ? primaryPCANField  : "",
          }}
          validationSchema={travelerSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}
        >
          {({
              values,
              errors,
              touched,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue
            }) => {
            return (
              <>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-md-4 descText">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.personal"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.descr2"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        <Row>
                          <div className={`${classes.inputContainerMembership} ${classes.rolesWrapper}`}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.aadvantage"/>
                            </label>
                            <div>
                              <AALogoSmall />
                              <Field
                                placeholder={intl.formatMessage(messages.aadvantagePlaceholder)}
                                type="text"
                                name="aadvantage"
                                id="aadvantage"
                                className={
                                  errors.aadvantage && touched.aadvantage
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                            </div>
                            <ErrorMessage
                              name="aadvantage"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>
                        <Row>
                          <Col xs={12} xl={6}>
                            <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.firstName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                type="text"
                                name="firstName"
                                id="firstName"
                                className={
                                  errors.firstName && touched.firstName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              <ErrorMessage
                                name="firstName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                          <Col xs={12} xl={6}>
                            <div className={`${classes.inputContainer} ${classes.rolesWrapper}`}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.lastName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                type="text"
                                name="lastName"
                                id="lastName"
                                className={
                                  errors.lastName && touched.lastName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              <ErrorMessage
                                name="lastName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <div className={classes.inputMailContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.emailAddress"/>
                            </label>
                            <div className={classes.editDiv}>
                              <Field
                                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                type="text"
                                name="emailAddress"
                                id="emailAddress"
                                className={
                                  errors.emailAddress && touched.emailAddress
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              {checkPermission("F_EDIT_MY_EMAILADDRESS" || "F_EDIT_OTHERS_EMAILADDRESS") &&
                                <OverlayTrigger
                                  placement="top"
                                  trigger={!travelerDetails?.allowEditEmail ? ['focus', 'click'] : []}
                                  overlay={
                                    <Tooltip id="email" className={`${general.main} ${classes.emailTooltip}`}>
                                      <div><FormattedMessage id="updateEmail.tooltip"/></div>
                                    </Tooltip>
                                  }
                                >
                                  <span>
                                    <UCButton
                                      onClick={() => setShowUpdateEmailModal(true)}
                                      className={classes.editButton}
                                      iconEnd={<EditEmailSvg/>}
                                      disabled={!travelerDetails?.allowEditEmail || travelerDetails?.userStatus === "REMOVED"}
                                    />
                                  </span>
                                </OverlayTrigger>
                              }
                            </div>
                            <ErrorMessage
                              name="emailAddress"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>                        
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  {checkPermission("F_COMPANY_EDITCITI_INFO") && travelerDetails?.companyInfo?.companyType === "Citi" &&
                    <>
                      <div className="row formDetailsBlock">
                        <div className="col-12 col-md-4 descText">
                          <div className={classes.personalDetails}>
                            <FormattedMessage id="traveler.details.citi"/>
                          </div>
                          <div className={classes.description}>
                            <FormattedMessage id="traveler.details.citiDescription"/>
                          </div>
                        </div>
                        <div className="col-12 col-md-8">
                          <div className={classes.formContainer}>
                            {
                              <Row>
                                <div className={classes.inputContainer}>
                                  <label htmlFor="primaryPCAN" className={classes.label}>
                                    <FormattedMessage id="traveler.details.entityPCAN"/>
                                    <span><FormattedMessage id="general.optional"/></span>
                                  </label>
                                  <Field
                                    placeholder={intl.formatMessage(messages.entityPCANPlaceholderMessage)}
                                    type="text"
                                    name="primaryPCANN"
                                    id="primaryPCAN"
                                    className={
                                      errors.primaryPCAN && touched.primaryPCAN
                                        ? "input-error"
                                        : null
                                    }
                                    disabled={true}
                                  />
                                  <ErrorMessage
                                    name="primaryPCAN"
                                    component="span"
                                    className={classes.error}
                                  />
                                </div>
                              </Row>
                            }
                            <Row>
                              <div className={classes.inputContainer}>
                                <label htmlFor="auPCAN" className={classes.label}>
                                  <FormattedMessage id="traveler.details.aupcan"/>
                                  <span><FormattedMessage id="general.optional"/></span>
                                </label>
                                <Field
                                  placeholder={intl.formatMessage(messages.aupcanPlaceholderMessage)}
                                  type="text"
                                  name="auPCAN"
                                  id="auPCAN"
                                  className={
                                    errors.auPCAN && touched.auPCAN
                                      ? "input-error"
                                      : null
                                  }
                                  disabled={true}
                                />
                                <ErrorMessage
                                  name="auPCAN"
                                  component="span"
                                  className={classes.error}
                                />
                              </div>
                            </Row>
                            <Row>
                              <div className={classes.editPCANDiv}>
                                <UCButton
                                  onClick={() => setShowEditPcaModal(true)}
                                  className={classes.editPCANButton}
                                  text={<FormattedMessage id='userProfile.editPCANButton'/>}
                                />
                              </div>
                            </Row>
                          </div>
                        </div>
                      </div>
                      <div className={classes.dividerContainer}>
                        <hr className={classes.border}/>
                      </div>
                    </>
                  }
                  
                  <div className="row">
                    <div className="col-12 col-md-4 descText">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.membership"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.desc"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        <Row>
                          <div className={classes.inputContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.role"/>
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip id="tooltipRole" className={`${general.main} ${classes.tooltip}`}>
                                    <div className={classes.roleTooltipTitle}><FormattedMessage id="traveler.details.roleTooltipTitle" /></div>
                                    <div className={classes.roleTooltipAdmin}><FormattedMessage id={`traveler.details.roleTooltipAdmin.${providerCode}`} /></div>
                                    <div className={classes.roleTooltipItem}><FormattedMessage id={`traveler.details.roleTooltipTraveler.${providerCode}`} /></div>
                                  </Tooltip>
                                }
                              >
                                <HelpIcon className={classes.helpIcon}/>
                              </OverlayTrigger>
                            </label>
                            {
                              roles.length > 0 &&
                              <Field
                                type="select"
                                name="roleId"
                                id="roleId"
                                render={({ form }) => (
                                  <div className={classes.rolesWrapper}>
                                    <UCSelect
                                      onMenuOpen={() => checkCanChangeRole()}
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="people.allRoles" />}
                                      options={roles}
                                      defaultValue={roles.find(item => parseInt(item.roleId) === parseInt(values.roleId))}
                                      onChange={(item) => form.setFieldValue("roleId", item.roleId)}
                                      getOptionLabel={(option) => option.roleName}
                                      getOptionValue={(option) => option.roleId}
                                      disabled={true}
                                    />
                                    <ErrorMessage
                                      name="roleId"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            }
                          </div>
                        </Row>
                        <Row>
                          <div className={classes.inputContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.team"/>
                            </label>
                            {
                              teams.length > 0 &&
                              <Field
                                type="select"
                                name="groupId"
                                id="groupId"
                                render={({ form }) => (
                                  <div className={classes.rolesWrapper}>
                                    <UCSelect
                                      className={classes.roles}
                                      displayName={<FormattedMessage id="people.allTeams" />}
                                      options={teams}
                                      defaultValue={teams.find(item => item.groupEntityId === values.groupId)}
                                      onChange={(item) => form.setFieldValue("groupId", item.groupEntityId)}
                                      getOptionLabel={option => option.groupName}
                                      getOptionValue={option => option.groupEntityId}
                                    />
                                    <ErrorMessage
                                      name="groupId"
                                      component="span"
                                      className={classes.error}
                                    />
                                  </div>
                                )}
                              />
                            }
                          </div>
                        </Row>
                        <div className={classes.checkboxContainer}>
                          <UCCheckbox
                            checked={tripLinkCheckbox}
                            onChange={() => setTripLinkCheckbox(!tripLinkCheckbox)}
                            label={<FormattedMessage id="traveler.details.triplink"/>}
                            className={classes.tripLink}
                          />
                          <OverlayTrigger
                            placement="top"
                            show={showTooltip}
                            overlay={
                              <Tooltip
                                  id="tooltipRole"
                                  className={`${general.main} ${classes.concurTooltip}`}
                                  onMouseEnter={handleMouseEnter}
                                  onMouseLeave={handleMouseLeave}
                                >
                                  <FormattedMessage id="traveler.details.tripLinkTooltip" />
                                  <Link to={getTripitLink(providerCode)} target="_blank">
                                    <FormattedMessage id="traveler.details.tripLinkTooltip.link" />
                                  </Link>
                                </Tooltip>
                            }
                          >
                            <HelpIcon 
                              className={classes.helpIcon}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                            />
                          </OverlayTrigger>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  <div className="row">
                    <div className="col-12 col-md-4 descText">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.notification"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.notificationDescription"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        {
                          travelerDetails?.showMarketingBusinessSummary && 
                          <>
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.business.title"/>}
                                description={<FormattedMessage id="traveler.details.business.description"/>}
                                checkboxChange={() => setBusinessSummaryCheckbox(!businessSummaryCheckbox)}
                                checked={businessSummaryCheckbox}
                              />
                            </Row>
                          </>
                        }
                        {
                          travelerDetails?.showMarketingBusinessSummary && (travelerDetails?.showMarketingPromotions || travelerDetails?.showMarketingNewsAndOffers ) &&
                          <div className={classes.dividerContainer}>
                            <hr className={classes.border}/>
                          </div>
                        } 
                        {
                          travelerDetails?.showMarketingPromotions && 
                          <>
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.promotions.title"/>}
                                description={<FormattedMessage id="traveler.details.promotions.description"/>}
                                checkboxChange={() => setPromotionCheckbox(!promotionCheckbox)}
                                checked={promotionCheckbox}
                              />
                            </Row>
                          </>
                        }
                        {
                          travelerDetails?.showMarketingPromotions && travelerDetails?.showMarketingNewsAndOffers &&
                          <div className={classes.dividerContainer}>
                            <hr className={classes.border}/>
                          </div>
                        }                        
                        { 
                          travelerDetails?.showMarketingNewsAndOffers && 
                          <>                          
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.newsandoffers.title"/>}
                                description={<FormattedMessage id="traveler.details.newsandoffers.description"/>}
                                checkboxChange={() => setNewsAndOffersCheckbox(!newsAndOffersCheckbox)}
                                checked={newsAndOffersCheckbox}
                              />
                            </Row>           
                          </>                        
                        }                          
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  <Row>
                    <Col>
                      <div className={classes.btnContainer}>
                        <UCButton
                          type="deleteLink"
                          className={classes.leaveButton}
                          text={<FormattedMessage id="traveler.details.leaveCompany"/>}
                          onClick={() => checkIfCanRemoveUser()}
                          icon={<DeleteUserIconRed />}
                        />
                      </div>
                    </Col>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.button}
                            text={<FormattedMessage id="traveler.details.cancel"/>}
                            onClick={goBack}
                          />
                          {
                            (checkPermission("F_EDIT_USER") ||
                              (travelerDetails?.userEntityId === userDetails?.userInfo?.userEntityId && checkPermission("F_EDIT_MYUSER"))
                            ) &&
                            <UCButton
                              type="primary"
                              onClick={handleFormSubmit}
                              text={<FormattedMessage id="traveler.details.save"/>}
                              spinner={{
                                name: "updatePeopleSpinner",
                                size: 20,
                                color: colors.white
                              }}
                            />
                          }
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              </>
            )
          }
        }
        </Formik>
      }
      <LeaveCompanyModal
        showModal={showLeaveCompanyModal}
        setShowModal={setShowLeaveCompanyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="traveler.details.leaveCompany.title" values={{name: `${travelerDetails?.companyInfo?.companyName}`}}/> }
        modalDescription={ <FormattedMessage id="traveler.details.leaveCompany.description" /> }
        submitCallback={leaveAction}
      />      
      <WarningModal
        showModal={showRemoveAdminOnlyModal}
        setShowModal={setShowRemoveAdminOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeAdminOnlyModal.title"/> }
        modalDescription={ <FormattedMessage id="removeAdminOnlyModal.description" /> }
      />
      <WarningModal
        showModal={showRemoveOwnerOnlyModal}
        setShowModal={setShowRemoveOwnerOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeOwnerOnlyModal.title"/> }
        modalDescription={ 
          <div className={classes.changeRoleModal}>
            <p><FormattedMessage id="removeOwnerOnlyModal.description1" /></p>
            <p><FormattedMessage id="removeOwnerOnlyModal.description2" /></p>
          </div> 
        }
      />      
      <WarningModal
        showModal={showRemoveCitiOnlyModal}
        setShowModal={setShowRemoveCitiOnlyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeCitiUser.title"/> }
        modalDescription={ <FormattedMessage id="removeCitiOnlyModal.description" /> }
      />
      <UpdateEmailModal
        setShowUpdateEmailModal={setShowUpdateEmailModal}
        showUpdateEmailModal={showUpdateEmailModal}
        travelerDetails={ travelerDetails }
      />
      <EditCitiPCANsModal
        showEditPcanModal={showEditPcanModal}
        setShowEditPcaModal={setShowEditPcaModal}
        userId={travelerDetails?.userEntityId}
        setPrimaryField={setPrimaryField}
        setAuPCANField={setAuPCANField}
        primaryPCAN={primaryPCANField}
        auPCAN={auPCANField}
        entityPCANLimit={travelerDetails?.entityPCANLimit}
      />
    </div>
  )
}

export default PersonalProfileAA;
